import { useEffect, useRef, useCallback } from 'react'
import { onSocketEvent } from './useWebSocket'
import { useAuth } from './useAuth'
import { useBrand } from './useBrand'
import api from '@/lib/api'
import { detectBrowserTimezone } from '@/lib/timezones'

interface NotificationPrefs {
  // Master switches
  soundEnabled: boolean
  desktopNotifications: boolean

  // Per-event sound
  newMessageSound: boolean
  chatRequestSound: boolean
  teamMessageSound: boolean
  humanEscalationSound: boolean
  bookingCreatedSound: boolean
  bookingReminderSound: boolean
  callbackExhaustedSound: boolean
  affiliateEmailSound: boolean

  // Per-event desktop
  newMessageDesktop: boolean
  chatRequestDesktop: boolean
  teamMessageDesktop: boolean
  humanEscalationDesktop: boolean
  bookingCreatedDesktop: boolean
  bookingReminderDesktop: boolean
  callbackExhaustedDesktop: boolean
  affiliateEmailDesktop: boolean
}

const DEFAULT_PREFS: NotificationPrefs = {
  soundEnabled: true,
  desktopNotifications: true,

  newMessageSound: true,
  chatRequestSound: true,
  teamMessageSound: true,
  humanEscalationSound: true,
  bookingCreatedSound: true,
  bookingReminderSound: true,
  callbackExhaustedSound: true,
  affiliateEmailSound: true,

  newMessageDesktop: true,
  chatRequestDesktop: true,
  teamMessageDesktop: true,
  humanEscalationDesktop: true,
  bookingCreatedDesktop: true,
  bookingReminderDesktop: true,
  callbackExhaustedDesktop: true,
  affiliateEmailDesktop: true,
}

// Singleton AudioContext — created on first user interaction so the browser allows it.
// Reused for all sounds. Resumed if browser auto-suspends it.
let audioCtx: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      audioCtx = new AudioContext()
    }
    // Resume if suspended (browsers suspend after inactivity)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume()
    }
    return audioCtx
  } catch {
    return null
  }
}

// Initialize AudioContext on first user interaction (click, key, touch)
// This unlocks audio playback even when notifications arrive later while tab is idle.
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    getAudioContext()
    window.removeEventListener('click', unlockAudio)
    window.removeEventListener('keydown', unlockAudio)
    window.removeEventListener('touchstart', unlockAudio)
  }
  window.addEventListener('click', unlockAudio)
  window.addEventListener('keydown', unlockAudio)
  window.addEventListener('touchstart', unlockAudio)
}

// Generate notification sounds using Web Audio API (no files needed)
function playTone(frequency: number, duration: number, type: OscillatorType = 'sine', volume = 0.3) {
  try {
    const ctx = getAudioContext()
    if (!ctx) return
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.value = frequency
    gain.gain.value = volume
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + duration)
  } catch {
    // Audio not available
  }
}

function playMessageSound() {
  // Two-tone "ding" — pleasant, short
  playTone(800, 0.15, 'sine', 0.25)
  setTimeout(() => playTone(1000, 0.2, 'sine', 0.2), 100)
}

function playBookingReminderSound() {
  // Distinctive chime — two rounds of ascending tones with a pause
  // Round 1
  playTone(523, 0.15, 'sine', 0.3) // C
  setTimeout(() => playTone(659, 0.15, 'sine', 0.3), 150) // E
  setTimeout(() => playTone(784, 0.25, 'sine', 0.3), 300) // G
  // Round 2 (after pause)
  setTimeout(() => {
    playTone(523, 0.15, 'sine', 0.3)
    setTimeout(() => playTone(659, 0.15, 'sine', 0.3), 150)
    setTimeout(() => playTone(784, 0.25, 'sine', 0.3), 300)
  }, 800)
}

function playHumanRequestSound() {
  // Urgent alarm — 3 rapid high-pitched pulses, pause, then 3 more
  // Much louder and more attention-grabbing than other sounds
  const pulse = (delay: number) => {
    setTimeout(() => playTone(1200, 0.1, 'square', 0.4), delay)
    setTimeout(() => playTone(900, 0.1, 'square', 0.35), delay + 120)
  }
  // First burst
  pulse(0)
  pulse(280)
  pulse(560)
  // Second burst after pause
  pulse(1100)
  pulse(1380)
  pulse(1660)
}

// Notification icon follows the resolved brand. Module-level because
// showDesktopNotification is called from socket-event closures; the hook
// keeps it in sync with useBrand() below.
let notificationIconUrl: string | undefined = '/legallady-logo.png'

function showDesktopNotification(title: string, body: string, onClick?: () => void, urgent?: boolean) {
  if (Notification.permission !== 'granted') return
  // For urgent: show even if tab is focused. For others: only when not focused.
  if (!urgent && document.hasFocus()) return

  try {
    const n = new Notification(title, {
      body,
      icon: notificationIconUrl,
      tag: urgent ? 'alert-urgent' : 'alert-' + Date.now(),
      requireInteraction: urgent || false, // Urgent stays until dismissed
    })
    if (onClick) {
      n.onclick = () => {
        window.focus()
        onClick()
        n.close()
      }
    }
    // Non-urgent auto-close after 5 seconds; urgent stays until clicked
    if (!urgent) {
      setTimeout(() => n.close(), 5000)
    }
  } catch {
    // Desktop notifications not supported
  }
}

export default function useAlertNotifications() {
  const { agent } = useAuth()
  const brand = useBrand()
  const prefs = useRef<NotificationPrefs>(DEFAULT_PREFS)
  const prefsLoaded = useRef(false)

  // Keep the desktop-notification icon in sync with the resolved brand
  useEffect(() => {
    notificationIconUrl = brand.logoUrl ?? undefined
  }, [brand.logoUrl])

  // Load preferences once
  useEffect(() => {
    if (prefsLoaded.current) return
    prefsLoaded.current = true
    api.get('/api/auth/preferences').then(res => {
      if (res.data.success && res.data.preferences) {
        const p = res.data.preferences
        // humanEscalationAlert is the legacy name for humanEscalationSound
        const escalationSound = p.humanEscalationSound ?? p.humanEscalationAlert
        prefs.current = {
          soundEnabled: p.soundEnabled ?? DEFAULT_PREFS.soundEnabled,
          desktopNotifications: p.desktopNotifications ?? DEFAULT_PREFS.desktopNotifications,

          newMessageSound: p.newMessageSound ?? DEFAULT_PREFS.newMessageSound,
          chatRequestSound: p.chatRequestSound ?? DEFAULT_PREFS.chatRequestSound,
          teamMessageSound: p.teamMessageSound ?? DEFAULT_PREFS.teamMessageSound,
          humanEscalationSound: escalationSound ?? DEFAULT_PREFS.humanEscalationSound,
          bookingCreatedSound: p.bookingCreatedSound ?? DEFAULT_PREFS.bookingCreatedSound,
          bookingReminderSound: p.bookingReminderSound ?? DEFAULT_PREFS.bookingReminderSound,
          callbackExhaustedSound: p.callbackExhaustedSound ?? DEFAULT_PREFS.callbackExhaustedSound,
          affiliateEmailSound: p.affiliateEmailSound ?? DEFAULT_PREFS.affiliateEmailSound,

          newMessageDesktop: p.newMessageDesktop ?? DEFAULT_PREFS.newMessageDesktop,
          chatRequestDesktop: p.chatRequestDesktop ?? DEFAULT_PREFS.chatRequestDesktop,
          teamMessageDesktop: p.teamMessageDesktop ?? DEFAULT_PREFS.teamMessageDesktop,
          humanEscalationDesktop: p.humanEscalationDesktop ?? DEFAULT_PREFS.humanEscalationDesktop,
          bookingCreatedDesktop: p.bookingCreatedDesktop ?? DEFAULT_PREFS.bookingCreatedDesktop,
          bookingReminderDesktop: p.bookingReminderDesktop ?? DEFAULT_PREFS.bookingReminderDesktop,
          callbackExhaustedDesktop: p.callbackExhaustedDesktop ?? DEFAULT_PREFS.callbackExhaustedDesktop,
          affiliateEmailDesktop: p.affiliateEmailDesktop ?? DEFAULT_PREFS.affiliateEmailDesktop,
        }
      }
    }).catch(() => {})
  }, [])

  // Request desktop notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // visitor:connected alerts disabled — too noisy, fires when someone just lands on the page
  // Only visitor:message (below) triggers sound/desktop notifications

  // Listen for incoming visitor messages
  useEffect(() => {
    return onSocketEvent('visitor:message', (data: any) => {
      if (data.senderType === 'bot' || data.senderType === 'agent' || data.senderType === 'system') return

      if (prefs.current.soundEnabled && prefs.current.newMessageSound) {
        playMessageSound()
      }
      if (prefs.current.desktopNotifications && prefs.current.newMessageDesktop) {
        showDesktopNotification(
          'New Message',
          data.message?.slice(0, 100) || 'You have a new message.',
        )
      }
    })
  }, [])

  // Listen for team messages — skip alerts for own messages (multi-device echo)
  useEffect(() => {
    return onSocketEvent('team:message', (data: any) => {
      if (data.senderId === agent?.id) return // Don't alert for your own messages

      if (prefs.current.soundEnabled && prefs.current.teamMessageSound) {
        playMessageSound()
      }
      if (prefs.current.desktopNotifications && prefs.current.teamMessageDesktop) {
        showDesktopNotification(
          `Message from ${data.senderName || 'Team member'}`,
          data.content?.startsWith('[image:') ? 'Sent an image' : (data.content?.slice(0, 100) || 'You have a new team message.'),
          () => { window.location.href = `/messages?contact=${encodeURIComponent(data.senderId)}` }
        )
      }
    })
  }, [agent?.id])

  // Listen for human request — visitor wants to speak with a real person
  // Handles both the dedicated event and the backup notification path
  const handleHumanRequest = useCallback((data: any) => {
    // If the user has turned off this specific alert entirely, skip
    if (!prefs.current.humanEscalationSound && !prefs.current.humanEscalationDesktop) return

    // Play urgent sound — always, even if tab is focused
    if (prefs.current.soundEnabled && prefs.current.humanEscalationSound) {
      playHumanRequestSound()
      // Repeat sound every 5 seconds up to 3 times if tab not focused
      let repeats = 0
      const repeatInterval = setInterval(() => {
        repeats++
        if (repeats >= 3 || document.hasFocus()) {
          clearInterval(repeatInterval)
          return
        }
        playHumanRequestSound()
      }, 5000)
    }

    // Desktop notification — urgent: shows even when tab focused, stays until dismissed
    if (prefs.current.desktopNotifications && prefs.current.humanEscalationDesktop) {
      showDesktopNotification(
        'Human Requested!',
        'A visitor is asking to speak with a real person. Click to open chat.',
        () => { window.location.href = '/chats' },
        true // urgent
      )
    }

    // Dispatch event for visual banner in DashboardLayout + sidebar flash
    window.dispatchEvent(new CustomEvent('human-requested', { detail: data }))
  }, [])

  useEffect(() => {
    return onSocketEvent('visitor:requesting_human', handleHumanRequest)
  }, [handleHumanRequest])

  // Listen for real-time notification events (calls, leads, etc.)
  useEffect(() => {
    return onSocketEvent('notification', (data: any) => {
      // Special handling for human request (backup path)
      if (data.type === 'human_requested') {
        handleHumanRequest(data)
        return
      }

      // Also catch chat_waiting notifications from NotificationService
      if (data.type === 'chat_waiting' && data.priority === 'urgent') {
        handleHumanRequest(data)
        return
      }

      // Special handling for booking reminders
      if (data.type === 'booking_reminder') {
        // Respect BOTH the firm-level toggle (in payload) AND the user's
        // personal bookingReminderSound preference.
        const orgReminderSoundEnabled = data.data?.soundEnabled !== false
        if (prefs.current.soundEnabled && prefs.current.bookingReminderSound && orgReminderSoundEnabled) {
          playBookingReminderSound()
        }
        if (prefs.current.desktopNotifications && prefs.current.bookingReminderDesktop) {
          // Re-render the time client-side using the user's own timezone.
          // The notification carries appointmentTime as UTC ISO; the server-baked
          // body uses the firm's TZ which can be wrong for staff in other zones.
          const userTz = (agent as any)?.timezone || detectBrowserTimezone()
          const apptIso = data.data?.appointmentTime
          let body = data.body || 'You have an appointment coming up.'
          if (apptIso) {
            try {
              const apptDate = new Date(apptIso)
              const userTime = apptDate.toLocaleTimeString('en-US', {
                timeZone: userTz, hour: 'numeric', minute: '2-digit', hour12: true,
              })
              const itemLabel = data.data?.bookingType === 'task' ? 'Task' : 'Appointment'
              const phone = data.data?.phone
              body = `${itemLabel} ${userTime}.${phone ? ` Call ${phone}` : ''}`.trim()
            } catch {
              // Fallback to server-baked body
            }
          }
          showDesktopNotification(
            data.title || 'Upcoming Appointment',
            body,
            () => { window.location.href = '/bookings' }
          )
        }
        // Dispatch custom event so nav sidebar can flash
        window.dispatchEvent(new CustomEvent('booking-reminder', { detail: data }))
        return
      }

      // New booking created
      if (data.type === 'booking_made') {
        if (prefs.current.soundEnabled && prefs.current.bookingCreatedSound) {
          playMessageSound()
        }
        if (prefs.current.desktopNotifications && prefs.current.bookingCreatedDesktop) {
          showDesktopNotification(
            data.title || 'New Booking',
            data.body || 'A new booking has been created.',
            () => { window.location.href = '/bookings' }
          )
        }
        return
      }

      // Affiliate inbound email — gentle ding + optional desktop popup,
      // and dispatch an event so the sidebar refreshes its unread badge
      // immediately instead of waiting for the next 60s poll.
      if (data.type === 'affiliate_email_received') {
        if (prefs.current.soundEnabled && prefs.current.affiliateEmailSound) {
          playMessageSound()
        }
        if (prefs.current.desktopNotifications && prefs.current.affiliateEmailDesktop) {
          showDesktopNotification(
            data.title || 'New affiliate email',
            data.body || data.data?.preview || '',
            () => { window.location.href = '/affiliate/email' },
          )
        }
        window.dispatchEvent(new CustomEvent('affiliate-email-received', { detail: data }))
        return
      }

      // Callback retries exhausted
      if (data.type === 'callback_exhausted') {
        if (prefs.current.soundEnabled && prefs.current.callbackExhaustedSound) {
          playMessageSound()
        }
        if (prefs.current.desktopNotifications && prefs.current.callbackExhaustedDesktop) {
          showDesktopNotification(
            data.title || 'Callback Failed',
            data.body || 'All callback attempts failed. Follow up needed.',
            () => { window.location.href = '/follow-up-queue' }
          )
        }
        return
      }

      // Generic fallback for unknown high-priority notifications
      if (prefs.current.desktopNotifications) {
        showDesktopNotification(
          data.title || 'Notification',
          data.body || '',
        )
      }
      if (prefs.current.soundEnabled && (data.priority === 'high' || data.priority === 'urgent')) {
        playMessageSound()
      }
    })
  }, [])

  // Expose a method to update prefs in real-time (after saving in settings)
  const updatePrefs = useCallback((newPrefs: Partial<NotificationPrefs>) => {
    prefs.current = { ...prefs.current, ...newPrefs }
  }, [])

  return { updatePrefs }
}
